import type { TFunction } from 'i18next'
import { DateUtils, PhoneUtils } from '@pbt/pbt-ui-components'

import InvoiceType from '~/constants/InvoiceType'
import type {
  InvoiceLineItem,
  InvoiceLineItemGroup,
  InvoiceOrEstimate,
} from '~/types'

import type {
  DeliveryMethod,
  SendToRecipient,
} from '../constants/paymentRequestDialogV2Constants'

export function getPrimaryPatientIdForPaymentRequest(
  invoice: InvoiceOrEstimate,
): string | undefined {
  if (invoice.patientId != null && String(invoice.patientId).trim() !== '') {
    return String(invoice.patientId)
  }
  if (invoice.patient != null && String(invoice.patient).trim() !== '') {
    return String(invoice.patient)
  }
  const fromList = invoice.patients?.find((id) => id != null && id !== '')
  if (fromList != null) {
    return fromList
  }
  return undefined
}

export function formatSendToDisplay(
  sendTo: SendToRecipient[],
  deliveryMethod: DeliveryMethod,
): string {
  return sendTo
    .map((r) => {
      if (deliveryMethod === 'email') {
        return r.email?.trim() ? r.email : r.name
      }
      const contact = r.phone ? PhoneUtils.formatPhoneNumber(r.phone) : null
      return contact ? `${r.name} (${contact})` : r.name
    })
    .join(', ')
}

export interface PaymentRequestV2TemplateInterpolation {
  clientFirstName: string
  clinicName: string
  clinicPhone: string
  invoiceAmount: string
  petName: string
  visitPhrase: string
}

export function buildPaymentRequestV2Templates(
  t: TFunction,
  deliveryMethod: DeliveryMethod,
  invoice: InvoiceOrEstimate,
  values: PaymentRequestV2TemplateInterpolation,
): { message: string; subject: string } {
  const isEstimate = invoice.type === InvoiceType.ESTIMATE

  if (deliveryMethod === 'email') {
    if (isEstimate) {
      return {
        subject: t(
          'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.EMAIL_DEPOSIT_SUBJECT',
          {
            petName: values.petName,
          },
        ),
        message: t(
          'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.EMAIL_DEPOSIT_BODY',
          values,
        ),
      }
    }

    return {
      subject: t(
        'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.EMAIL_INVOICE_SUBJECT',
      ),
      message: t(
        'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.EMAIL_INVOICE_BODY',
        values,
      ),
    }
  }

  if (isEstimate) {
    return {
      subject: '',
      message: t(
        'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.SMS_DEPOSIT_BODY',
        values,
      ),
    }
  }

  return {
    subject: '',
    message: t(
      'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.SMS_INVOICE_BODY',
      values,
    ),
  }
}

type Nil = null | undefined

type TimetableEventWithScheduledStartDate = {
  scheduledStartDatetime?: string | null
}

export type PaymentRequestVisitFromTimetableResult = {
  dateDisplay: string
  patientIdForVisit: string | undefined
}

type VisitCandidate = { patientId: string; raw: string }

/** First line item wins when the same `eventId` appears on multiple rows. */
function eventIdToPatientIdFromLineItems(
  groups: InvoiceLineItemGroup[] | Nil,
): Record<string, string> {
  if (groups == null || groups.length === 0) {
    return {}
  }
  const map: Record<string, string> = {}
  groups.forEach((group) => {
    group.groupedItems?.forEach((item: InvoiceLineItem) => {
      const { eventId } = item
      if (eventId != null && eventId !== '' && map[eventId] === undefined) {
        map[eventId] = item.patientId
      }
    })
  })
  return map
}

function pickLatestVisitCandidate(
  candidates: VisitCandidate[],
): VisitCandidate | null {
  if (candidates.length === 0) {
    return null
  }
  let best = candidates[0]
  let bestMs = Date.parse(best.raw)
  for (let i = 1; i < candidates.length; i++) {
    const ms = Date.parse(candidates[i].raw)
    if (!Number.isNaN(ms) && (Number.isNaN(bestMs) || ms > bestMs)) {
      best = candidates[i]
      bestMs = ms
    }
  }
  return Number.isNaN(bestMs) ? null : best
}

/**
 * From line-item `eventId`s + Redux timetable: formatted visit date for templates
 * and `patientId` on the line item tied to the latest scheduled start that is
 * not in the future (future appointments are omitted). 
 
 * CVC-36750: For P0 (no scheduled date for estimates), `visitPhrase` is omitted from the template strings for estimates.
 */
export function getPaymentRequestVisitFromTimetable(
  invoice: InvoiceOrEstimate | Nil,
  timetableEventsMap:
    | Record<string, TimetableEventWithScheduledStartDate>
    | Nil,
): PaymentRequestVisitFromTimetableResult {
  if (invoice == null || timetableEventsMap == null) {
    return { dateDisplay: '', patientIdForVisit: undefined }
  }

  if (invoice.type === InvoiceType.ESTIMATE) {
    return { dateDisplay: '', patientIdForVisit: undefined }
  }

  const eventIdToPatientId = eventIdToPatientIdFromLineItems(invoice.groups)
  const candidates = Object.keys(eventIdToPatientId).flatMap(
    (eventId): VisitCandidate[] => {
      const raw = timetableEventsMap[eventId]?.scheduledStartDatetime
      if (raw == null) {
        return []
      }
      const s = String(raw).trim()
      if (s === '') {
        return []
      }
      const ms = Date.parse(s)
      if (Number.isNaN(ms) || ms > Date.now()) {
        return []
      }
      return [
        {
          patientId: eventIdToPatientId[eventId] ?? '',
          raw: s,
        },
      ]
    },
  )

  const best = pickLatestVisitCandidate(candidates)
  if (best == null) {
    return { dateDisplay: '', patientIdForVisit: undefined }
  }

  const trimmed = best.patientId.trim()
  return {
    dateDisplay: DateUtils.formatDate(best.raw),
    patientIdForVisit: trimmed === '' ? undefined : trimmed,
  }
}
